import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, User, Calendar, MapPin, Briefcase, GraduationCap, 
  Clock, Heart, Camera, Award, Users, Target, ArrowRight, Upload,
  CheckCircle, Loader2, Lock, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const educationLevels = [
  'High School Diploma / GED',
  'Some College',
  'Associate\'s Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctoral Degree',
  'Child Development Associate (CDA)',
  'Other Certification'
];

const experienceRanges = [
  'Less than 1 year',
  '1-2 years',
  '3-5 years',
  '6-10 years',
  '11-15 years',
  '16-20 years',
  'More than 20 years'
];

const skillCategories = [
  'Classroom Management',
  'Curriculum Development',
  'Special Needs Education',
  'Parent Communication',
  'Child Assessment',
  'Behavior Management',
  'Creative Arts',
  'STEM Education',
  'Literacy Development',
  'Social-Emotional Learning',
  'Leadership',
  'Mentoring',
  'Technology Integration',
  'Bilingual Education',
  'Other'
];

export default function JoinPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    dateOfBirth: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    maritalStatus: '',
    hasChildren: '',
    
    // Address
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Professional Information
    employer: '',
    jobTitle: '',
    educationLevel: '',
    yearsInECE: '',
    
    // Background & Interests
    whatBroughtYouToECE: '',
    skillsAndTalents: [],
    otherSkills: '',
    hobbiesAndInterests: '',
    willingToMentor: '',
    mentorReason: '',
    
    // Goals
    whatYouHopeToGain: '',
    
    // Conference Support
    willingSupportConference: '',
    willingServeCommittee: '',
    
    // Photo/Media Consent
    photoConsent: '',
    
    // Additional Information
    howDidYouHear: '',
    additionalInfo: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skillsAndTalents: prev.skillsAndTalents.includes(skill)
        ? prev.skillsAndTalents.filter(s => s !== skill)
        : [...prev.skillsAndTalents, skill]
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        if (!formData.fullName || !formData.email || !formData.dateOfBirth || !formData.password) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        return true;
      case 2:
        if (!formData.employer || !formData.jobTitle || !formData.educationLevel || !formData.yearsInECE) {
          toast.error('Please fill in all required fields');
          return false;
        }
        return true;
      case 3:
        if (!formData.whatBroughtYouToECE || formData.skillsAndTalents.length === 0 || !formData.willingToMentor) {
          toast.error('Please fill in all required fields');
          return false;
        }
        return true;
      case 4:
        if (!formData.whatYouHopeToGain) {
          toast.error('Please tell us what you hope to gain from joining');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for multipart submission
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'confirmPassword') return; // Skip confirm password
        if (key === 'skillsAndTalents') {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add profile image if present
      if (profileImage) {
        submitData.append('profileImage', profileImage);
      }
      
      const response = await fetch(`${API_URL}/api/members/register`, {
        method: 'POST',
        body: submitData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
      
      const data = await response.json();
      
      // Save token to localStorage for member login
      if (data.token) {
        localStorage.setItem('memberToken', data.token);
        localStorage.setItem('memberInfo', JSON.stringify({
          id: data.member_id,
          fullName: formData.fullName,
          email: formData.email
        }));
      }
      
      toast.success('Welcome to DC Men in ECE! Your membership has been approved.');
      navigate('/member-dashboard', { state: { registered: true } });
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Professional', icon: Briefcase },
    { number: 3, title: 'Background', icon: Heart },
    { number: 4, title: 'Goals', icon: Target },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-brand-navy to-navy-800">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <nav className="flex items-center justify-center gap-2 text-sm text-navy-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/membership" className="hover:text-white transition-colors">Membership</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Join</span>
          </nav>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Join <span className="text-brand-gold">DC Men in ECE</span>
          </h1>
          <p className="text-lg text-navy-200 max-w-2xl mx-auto">
            Become part of a supportive community dedicated to advancing men of color in early childhood education.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-navy-100">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted 
                          ? 'bg-brand-green text-white' 
                          : isActive 
                            ? 'bg-brand-gold text-navy-900' 
                            : 'bg-navy-100 text-navy-400'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`mt-2 text-xs font-medium hidden sm:block ${
                      isActive ? 'text-brand-gold' : isCompleted ? 'text-brand-green' : 'text-navy-400'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded-full transition-all duration-300 ${
                      currentStep > step.number ? 'bg-brand-green' : 'bg-navy-100'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form onSubmit={handleSubmit}>
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-navy-900">Personal Information</h2>
                    <p className="text-navy-500 text-sm">Tell us about yourself</p>
                  </div>
                </div>

                {/* Profile Photo Upload */}
                <div className="mb-8 flex flex-col items-center">
                  <div className="relative">
                    <div className={`w-32 h-32 rounded-full overflow-hidden border-4 border-navy-100 ${!profileImagePreview && 'bg-navy-50'} flex items-center justify-center`}>
                      {profileImagePreview ? (
                        <img src={profileImagePreview} alt="Profile preview" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-10 h-10 text-navy-300" />
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 w-10 h-10 bg-brand-gold rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-gold-600 transition-colors">
                      <Upload className="w-5 h-5 text-navy-900" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange}
                        className="hidden" 
                      />
                    </label>
                  </div>
                  <p className="text-sm text-navy-500 mt-3">Upload a profile photo (optional)</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="fullName" className="text-navy-700 font-medium">
                      Full Name <span className="text-brand-red">*</span>
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-fullname-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-navy-700 font-medium">
                      Email Address <span className="text-brand-red">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-email-input"
                    />
                  </div>

                  {/* Password Section */}
                  <div className="md:col-span-2 p-4 bg-navy-50 rounded-xl border border-navy-200">
                    <h4 className="font-medium text-navy-900 mb-4 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Create Your Account Password
                    </h4>
                    <p className="text-sm text-navy-600 mb-4">
                      This password will be used to log into your member account.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password" className="text-navy-700 font-medium">
                          Password <span className="text-brand-red">*</span>
                        </Label>
                        <div className="relative mt-2">
                          <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Minimum 8 characters"
                            className="pr-10 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                            data-testid="join-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword" className="text-navy-700 font-medium">
                          Confirm Password <span className="text-brand-red">*</span>
                        </Label>
                        <div className="relative mt-2">
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder="Re-enter password"
                            className="pr-10 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                            data-testid="join-confirm-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-navy-700 font-medium">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dateOfBirth" className="text-navy-700 font-medium">
                      Date of Birth <span className="text-brand-red">*</span>
                    </Label>
                    <Input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-dob-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="maritalStatus" className="text-navy-700 font-medium">Marital Status</Label>
                    <select
                      id="maritalStatus"
                      name="maritalStatus"
                      value={formData.maritalStatus}
                      onChange={handleInputChange}
                      className="mt-2 w-full h-10 px-3 rounded-md border border-navy-200 bg-white text-navy-900 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      data-testid="join-marital-select"
                    >
                      <option value="">Select status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-navy-700 font-medium">Do you have children?</Label>
                    <div className="flex gap-4 mt-2">
                      {['Yes', 'No'].map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, hasChildren: option }))}
                          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            formData.hasChildren === option
                              ? 'bg-brand-green text-white shadow-md'
                              : 'bg-navy-50 text-navy-600 hover:bg-navy-100'
                          }`}
                          data-testid={`join-children-${option.toLowerCase()}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="streetAddress" className="text-navy-700 font-medium">Street Address</Label>
                    <Input
                      id="streetAddress"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-address-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-navy-700 font-medium">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Washington"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-city-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state" className="text-navy-700 font-medium">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="DC"
                        className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                        data-testid="join-state-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="text-navy-700 font-medium">ZIP Code</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="20001"
                        className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                        data-testid="join-zip-input"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Professional Information */}
          {currentStep === 2 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-navy-900">Professional Information</h2>
                    <p className="text-navy-500 text-sm">Your career in early childhood education</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="employer" className="text-navy-700 font-medium">
                      Current Employer <span className="text-brand-red">*</span>
                    </Label>
                    <Input
                      id="employer"
                      name="employer"
                      value={formData.employer}
                      onChange={handleInputChange}
                      placeholder="Organization or School Name"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-employer-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="jobTitle" className="text-navy-700 font-medium">
                      Job Title <span className="text-brand-red">*</span>
                    </Label>
                    <Input
                      id="jobTitle"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleInputChange}
                      placeholder="e.g., Lead Teacher, Director"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-jobtitle-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="educationLevel" className="text-navy-700 font-medium">
                      Highest Education Level <span className="text-brand-red">*</span>
                    </Label>
                    <select
                      id="educationLevel"
                      name="educationLevel"
                      value={formData.educationLevel}
                      onChange={handleInputChange}
                      className="mt-2 w-full h-10 px-3 rounded-md border border-navy-200 bg-white text-navy-900 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      data-testid="join-education-select"
                    >
                      <option value="">Select education level</option>
                      {educationLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="yearsInECE" className="text-navy-700 font-medium">
                      Years in ECE <span className="text-brand-red">*</span>
                    </Label>
                    <select
                      id="yearsInECE"
                      name="yearsInECE"
                      value={formData.yearsInECE}
                      onChange={handleInputChange}
                      className="mt-2 w-full h-10 px-3 rounded-md border border-navy-200 bg-white text-navy-900 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      data-testid="join-years-select"
                    >
                      <option value="">Select experience</option>
                      {experienceRanges.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Background & Skills */}
          {currentStep === 3 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-navy-900">Background & Skills</h2>
                    <p className="text-navy-500 text-sm">Share your journey and expertise</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <Label htmlFor="whatBroughtYouToECE" className="text-navy-700 font-medium">
                      What brought you to early childhood education? <span className="text-brand-red">*</span>
                    </Label>
                    <Textarea
                      id="whatBroughtYouToECE"
                      name="whatBroughtYouToECE"
                      value={formData.whatBroughtYouToECE}
                      onChange={handleInputChange}
                      placeholder="Share your story - what inspired you to work in ECE?"
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green min-h-[120px]"
                      data-testid="join-ece-story-textarea"
                    />
                  </div>

                  <div>
                    <Label className="text-navy-700 font-medium mb-4 block">
                      Skills & Talents <span className="text-brand-red">*</span>
                      <span className="font-normal text-navy-500 ml-2">(Select all that apply)</span>
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {skillCategories.map(skill => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => handleSkillToggle(skill)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                            formData.skillsAndTalents.includes(skill)
                              ? 'bg-brand-green text-white shadow-md'
                              : 'bg-navy-50 text-navy-600 hover:bg-navy-100'
                          }`}
                          data-testid={`join-skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                    {formData.skillsAndTalents.includes('Other') && (
                      <Input
                        name="otherSkills"
                        value={formData.otherSkills}
                        onChange={handleInputChange}
                        placeholder="Please specify other skills..."
                        className="mt-4 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                        data-testid="join-other-skills-input"
                      />
                    )}
                  </div>

                  <div>
                    <Label htmlFor="hobbiesAndInterests" className="text-navy-700 font-medium">
                      Hobbies & Interests
                    </Label>
                    <Textarea
                      id="hobbiesAndInterests"
                      name="hobbiesAndInterests"
                      value={formData.hobbiesAndInterests}
                      onChange={handleInputChange}
                      placeholder="Share your hobbies and interests outside of work..."
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green min-h-[100px]"
                      data-testid="join-hobbies-textarea"
                    />
                  </div>

                  <div>
                    <Label className="text-navy-700 font-medium mb-4 block">
                      Are you willing to mentor other young men interested in working in ECE? <span className="text-brand-red">*</span>
                    </Label>
                    <div className="flex gap-4">
                      {['Yes', 'No', 'Maybe Later'].map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, willingToMentor: option }))}
                          className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                            formData.willingToMentor === option
                              ? 'bg-brand-gold text-navy-900 shadow-md'
                              : 'bg-navy-50 text-navy-600 hover:bg-navy-100'
                          }`}
                          data-testid={`join-mentor-${option.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {formData.willingToMentor === 'Yes' && (
                      <Textarea
                        name="mentorReason"
                        value={formData.mentorReason}
                        onChange={handleInputChange}
                        placeholder="What motivates you to mentor others? (Optional)"
                        className="mt-4 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                        data-testid="join-mentor-reason-textarea"
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Goals */}
          {currentStep === 4 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-navy to-navy-700 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-navy-900">Your Goals</h2>
                    <p className="text-navy-500 text-sm">What do you hope to achieve?</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="whatYouHopeToGain" className="text-navy-700 font-medium">
                      What do you hope to gain by joining DC Men in ECE? <span className="text-brand-red">*</span>
                    </Label>
                    <Textarea
                      id="whatYouHopeToGain"
                      name="whatYouHopeToGain"
                      value={formData.whatYouHopeToGain}
                      onChange={handleInputChange}
                      placeholder="Share your goals and expectations for membership..."
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green min-h-[150px]"
                      data-testid="join-goals-textarea"
                    />
                  </div>

                  <div>
                    <Label htmlFor="howDidYouHear" className="text-navy-700 font-medium">
                      How did you hear about DC Men in ECE?
                    </Label>
                    <select
                      id="howDidYouHear"
                      name="howDidYouHear"
                      value={formData.howDidYouHear}
                      onChange={handleInputChange}
                      className="mt-2 w-full h-10 px-3 rounded-md border border-navy-200 bg-white text-navy-900 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      data-testid="join-referral-select"
                    >
                      <option value="">Select an option</option>
                      <option value="colleague">Colleague / Word of Mouth</option>
                      <option value="social-media">Social Media</option>
                      <option value="conference">Conference / Event</option>
                      <option value="employer">Employer</option>
                      <option value="search">Internet Search</option>
                      <option value="partner-org">Partner Organization</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="additionalInfo" className="text-navy-700 font-medium">
                      Is there anything else you'd like us to know?
                    </Label>
                    <Textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      placeholder="Any additional information, questions, or special considerations..."
                      className="mt-2 border-navy-200 focus:border-brand-green focus:ring-brand-green"
                      data-testid="join-additional-textarea"
                    />
                  </div>

                  {/* Conference Support */}
                  <div className="p-6 bg-gold-50 rounded-2xl border border-gold-200">
                    <h3 className="font-bold text-navy-900 mb-4">Annual Conference Support</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-navy-700 font-medium mb-3 block">
                          Are you willing to support the annual DC Men in ECE Citywide Conference?
                        </Label>
                        <div className="flex gap-4">
                          {['Yes', 'No', 'Maybe'].map(option => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, willingSupportConference: option }))}
                              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                formData.willingSupportConference === option
                                  ? 'bg-brand-gold text-navy-900 shadow-md'
                                  : 'bg-white text-navy-600 hover:bg-navy-50 border border-navy-200'
                              }`}
                              data-testid={`join-conference-${option.toLowerCase()}`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>

                      {formData.willingSupportConference === 'Yes' && (
                        <div className="mt-4 pt-4 border-t border-gold-200">
                          <Label className="text-navy-700 font-medium mb-3 block">
                            Are you willing to serve on a committee?
                          </Label>
                          <div className="flex gap-4">
                            {['Yes', 'No'].map(option => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, willingServeCommittee: option }))}
                                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.willingServeCommittee === option
                                    ? 'bg-brand-green text-white shadow-md'
                                    : 'bg-white text-navy-600 hover:bg-navy-50 border border-navy-200'
                                }`}
                                data-testid={`join-committee-${option.toLowerCase()}`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photo/Media Consent */}
                  <div className="p-6 bg-navy-50 rounded-2xl border border-navy-200">
                    <h3 className="font-bold text-navy-900 mb-2">Photo & Media Consent</h3>
                    <p className="text-sm text-navy-600 mb-4">
                      DC Men in ECE may feature photos and videos of members in promotional materials, social media, and other communications to advance our mission.
                    </p>
                    
                    <div>
                      <Label className="text-navy-700 font-medium mb-3 block">
                        Do you agree to allow your photo or image to be featured in videos and materials related to the promotion of DC Men in ECE? <span className="text-brand-red">*</span>
                      </Label>
                      <div className="flex gap-4">
                        {['Yes', 'No'].map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, photoConsent: option }))}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              formData.photoConsent === option
                                ? option === 'Yes' 
                                  ? 'bg-brand-green text-white shadow-md'
                                  : 'bg-brand-red text-white shadow-md'
                                : 'bg-white text-navy-600 hover:bg-navy-50 border border-navy-200'
                            }`}
                            data-testid={`join-photo-consent-${option.toLowerCase()}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Summary Preview */}
                  <div className="mt-8 p-6 bg-navy-50 rounded-2xl">
                    <h3 className="font-bold text-navy-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-green" />
                      Ready to Submit
                    </h3>
                    <p className="text-navy-600 text-sm">
                      By submitting this form, you're taking the first step to join our community of dedicated early childhood educators and professionals. 
                      We'll review your application and be in touch soon with next steps.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50 px-8"
                data-testid="join-prev-button"
              >
                Previous
              </Button>
            ) : (
              <div />
            )}
            
            {currentStep < 4 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-lg"
                data-testid="join-next-button"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-brand-green hover:bg-green-600 text-white font-semibold px-8 shadow-lg disabled:opacity-50"
                data-testid="join-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
